import { z } from "zod";
import { nanoid, nanoidSchema } from "../utility/index.js";
const headerLinkBaseSchema = z.object({
  uid: nanoidSchema,
  title: z.string(),
  show: z.boolean().default(true),
  isStarred: z.boolean().default(false),
  type: z.union([
    z.literal("guide"),
    z.literal("reference"),
    z.literal("link"),
    z.literal("group"),
    z.literal("version-selector"),
    z.literal("spacer")
  ]).default("link")
}).meta({ id: "header-link-base" });
const headerLinkReferenceSchema = headerLinkBaseSchema.extend({
  type: z.literal("reference"),
  /** Whether the reference has a permalinked reference or not */
  hasPermalink: z.boolean().default(false).describe("Whether the reference has a permalinked reference or not"),
  hasManagedDoc: z.boolean().default(false).describe("Whether the reference has a linked registry API")
}).meta({ id: "header-link-reference" });
const headerLinkGuideSchema = headerLinkBaseSchema.extend({
  type: z.literal("guide")
}).meta({ id: "header-link-guide" });
const headerLinkVersionSelectorSchema = headerLinkBaseSchema.extend({
  type: z.literal("version-selector")
}).meta({ id: "header-link-version-selector" });
const headerLinkSpacerSchema = headerLinkBaseSchema.extend({
  type: z.literal("spacer")
}).meta({ id: "header-link-spacer" });
const headerLinkExternalSchema = headerLinkBaseSchema.extend({
  type: z.literal("link"),
  isButton: z.boolean().default(false),
  link: z.string()
}).meta({ id: "header-link-external" });
const headerLinkGroupSchema = headerLinkBaseSchema.extend({
  type: z.literal("group"),
  /** Children of the group. Groups cannot contain other groups, spacers, or the version selector */
  children: z.array(
    z.union([
      headerLinkGuideSchema,
      headerLinkReferenceSchema,
      headerLinkExternalSchema
    ])
  ).describe(
    "Children of the group. Groups cannot contain other groups, spacers, or the version selector"
  )
}).meta({ id: "header-link-group" });
const headerLinkSchema = z.union([
  headerLinkGuideSchema,
  headerLinkReferenceSchema,
  headerLinkGroupSchema,
  headerLinkExternalSchema,
  headerLinkVersionSelectorSchema,
  headerLinkSpacerSchema
]).meta({ id: "header-link" });
function getHeaderLinks({
  guides,
  references,
  header
}) {
  const complete = [...header].filter((h) => {
    if (h.type === "guide") return !!guides[h.uid];
    if (h.type === "reference") return !!references[h.uid];
    return true;
  }).map((header2) => {
    if (header2.type !== "group") return header2;
    return {
      ...header2,
      // Only include header children if the guide/reference is available
      children: header2.children.filter((c) => {
        if (c.type === "guide") return !!guides[c.uid];
        if (c.type === "reference") return !!references[c.uid];
        return true;
      })
    };
  });
  const orderedIds = [];
  complete.forEach((h) => {
    orderedIds.push(h.uid);
    if (h.type === "group") {
      orderedIds.push(...h.children.map((c) => c.uid));
    }
  });
  Object.values(guides).forEach((g) => {
    if (!orderedIds.includes(g.uid)) {
      complete.push({
        uid: g.uid,
        title: g.title,
        show: g.show,
        isStarred: g.isStarred,
        type: "guide"
      });
    }
  });
  Object.values(references).forEach((r) => {
    if (!orderedIds.includes(r.uid)) {
      complete.push({
        uid: r.uid,
        title: r.title,
        show: r.show,
        isStarred: r.isStarred,
        type: "reference",
        hasPermalink: !!r.specPermalink,
        hasManagedDoc: !!r.managedDoc
      });
    }
  });
  if (!complete.find((e) => e.type === "version-selector")) {
    complete.push({
      uid: nanoid(),
      title: "Version Selector",
      type: "version-selector",
      show: true,
      isStarred: false
    });
  }
  complete.forEach((element) => {
    const guide = guides[element.uid];
    const reference = references[element.uid];
    if (element.type === "guide" && guide) {
      element.title = guide.title;
      element.show = guide.show;
      element.isStarred = guide.isStarred;
    }
    if (element.type === "reference" && reference) {
      element.title = reference.title;
      element.show = reference.show;
      element.isStarred = reference.isStarred;
      element.hasPermalink = !!reference.specPermalink;
      element.hasManagedDoc = !!reference.managedDoc;
    }
    if (element.type === "group") {
      element.children.forEach((c) => {
        const childGuide = guides[c.uid];
        const childReference = references[c.uid];
        if (c.type === "guide" && childGuide) {
          c.title = childGuide.title;
          c.show = childGuide.show;
          c.isStarred = childGuide.isStarred;
        }
        if (c.type === "reference" && childReference) {
          c.title = childReference.title;
          c.show = childReference.show;
          c.isStarred = childReference.isStarred;
          c.hasPermalink = !!childReference.specPermalink;
          c.hasManagedDoc = !!childReference.managedDoc;
        }
      });
    }
  });
  return complete;
}
export {
  getHeaderLinks,
  headerLinkSchema
};
//# sourceMappingURL=header-links.js.map
